import WixQuestionEditor from '../components/WixQuestionEditor';
import { prepareWixBrand } from '../utils/wixPreparation';
/**
 * WixOnboarding.js — the NEW Wix onboarding flow (built from scratch).
 *
 * Lives at /wix-start, beside (not replacing) the legacy /wix-register flow.
 *   0. Account  — create account or log in (Wix instance from URL params)
 *   1. Connect  — optional URL (pre-filled). We INSTANTLY create a ready bot
 *                 (no scrape), detect the site's Wix sub-apps and show the skill
 *                 picker right away. If a URL was given, the website is learned in
 *                 the BACKGROUND (find_new_links -> scrape_link) with a docked
 *                 progress strip; we never block on it.
 *   2. Design   — customize the widget (header, welcome, colors, icon) + preview.
 *   3. Test     — chat with the agent live (Wix skills work immediately).
 *   4. (placeholder — notifications + free trial come next)
 *
 * - i18n via wxo_* keys; sub-app/flow labels reuse the Studio's skill_flow_wix_*
 *   / wix_sub_* keys. Language dropdown top-right.
 * - Real runs persist progress (localStorage + DB); ?demo=1 NEVER remembers.
 * - Page never scrolls (fixed shell + body lock); the card body scrolls.
 * Styling: WixOnboarding.css (wxo- scoped). Brand #5616ea, DM Sans. No "chatbot".
 *
 * NOTE: the DEMO_* blocks + ?demo=1 are a no-backend design preview — remove
 *       before launch.
 */
import { trackRegistration, beginWixCheckout, observeWixTrial } from '../utils/metaTracking';
import { useState, useEffect, useRef, useCallback, useMemo, cloneElement } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField, Checkbox, IconButton, InputAdornment, Tooltip } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight, faArrowLeft, faArrowUp, faGlobe, faCheck,
  faUpload, faXmark, faFileArrowUp, faFileLines, faPlus,
  faComments, faHeadset, faUserPlus, faEnvelope, faDesktop, faTrash, faTriangleExclamation,
  faBookOpen, faSliders, faMinus, faCode, faLink, faBolt, faDatabase, faEllipsis,
  faPuzzlePiece,
} from '@fortawesome/free-solid-svg-icons';
import { faTelegram } from '@fortawesome/free-brands-svg-icons';
import ReactMarkdown from 'react-markdown';
import ChatModal from '../components/chatModal.js';
import WixAccountForm from '../components/WixAccountForm';
import UploadFileModal from '../components/uploadFileModal.js';
import { SlackBrandIcon, TeamsBrandIcon, TelegramBrandIcon } from '../components/BrandIcons';
import SlackLogo from '../components/SlackLogo.js';
import { CHANNEL_GUIDES } from '../data/channelGuides.js';
import WixEntryLoader from '../components/WixEntryLoader.js';
import { SUPPORTED_LANGUAGES } from '../translations.js';
import { authFetch } from '../utils/api';
import { markSingleAgentEntry } from '../utils/singleAgentEntry.js';
import './WixOnboarding.css';

const API = process.env.REACT_APP_API_BASE_URL;
// Onboarding step + agent are DB-only (see /api/wix/onboarding/state + /progress).
// Nothing about the flow is kept in localStorage, so it survives device changes and
// cleared browser storage. The only localStorage value is the auth token (re-minted by
// /resolve on every Wix entry).

const FIELD_SX = {
  backgroundColor: '#fff',
  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
  '& .MuiOutlinedInput-root.Mui-focused fieldset': { border: '1px solid #515151' },
  '& .MuiInputLabel-root': { color: 'gray' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#5616ea' },
};

const DEFAULT_WIDGET = {
  themeColor: '#5616ea',
  headerColor: '#ffffff',
  hoverColor: '#4413c4',
  widgetHorizontalAlignment: 'right',
  widgetSize: 62,
  widgetBorderRadius: 50,
  headerText: 'Chat with us',
  welcomeMessage: 'Hi, how can I assist you?',
  predefinedQuestions: [],
  pulsing: true,
  icon: '',   // widget launcher icon (widget_icon_path)
  logo: '',   // logo shown in the chat header (header_icon_path)
  avatar: '', // avatar shown next to agent replies (avatar_icon_path)
};

// ?demo=1 is a LOCAL TEST entry: it bypasses the Wix App Market redirect
// handshake (so you can reach the flow without installing from Wix) but runs the
// REAL backend — it creates a real bot, real detection and real training against
// this known test instance (VivaLoria). It only skips progress PERSISTENCE, so a
// refresh starts fresh. Point it at a different instance to test another site.
const DEMO_INSTANCE = 'b9f7fb4b-35f8-4445-bbaf-24a5549f5d53';

// Knowledge sources are selectable independently from executable skills.
const ALWAYS_ON_FLOW = 'wix_products';
const CMS_FLOW = 'wix_cms';
const KNOWLEDGE_FLOWS = new Set([ALWAYS_ON_FLOW, CMS_FLOW]);

// The two completion strips ("Learned N pages", "Imported N products") auto-dismiss
// this long after finishing, with a circular ring that depletes over the same time.
const DONE_HIDE_MS = 5000;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ── Step 4 — notification matrix (events x channels) + email recipients.
//    Structure mirrors the legacy WixRegister "engagement" step. ──
const WXO_NOTIFY_CHANNELS = [
  { key: 'in_app', icon: faDesktop, labelKey: 'wxo_notif_ch_inapp' },
  { key: 'email', icon: faEnvelope, labelKey: 'wxo_notif_ch_email' },
];
const WXO_NOTIFY_EVENTS = [
  { key: 'new_chat', section: 'live_chat', icon: faComments, titleKey: 'wxo_notif_chat_title', descKey: 'wxo_notif_chat_desc' },
  { key: 'help_requested', section: 'live_chat', icon: faHeadset, titleKey: 'wxo_notif_help_title', descKey: 'wxo_notif_help_desc', highlight: true },
  { key: 'new_lead', section: 'leads', icon: faUserPlus, titleKey: 'wxo_notif_lead_title', descKey: 'wxo_notif_lead_desc' },
  { key: 'new_message', section: 'live_chat', icon: faEnvelope, titleKey: 'wxo_notif_msg_title', descKey: 'wxo_notif_msg_desc' },
];
const WXO_LOCKED_CHANNELS = { new_message: ['email'] }; // joined-chat replies: in-app only, no email spam
const wxoChannelLocked = (eventKey, channelKey) => (WXO_LOCKED_CHANNELS[eventKey] || []).includes(channelKey);
const WXO_DEFAULT_PREFS = {
  new_chat: { in_app: true, email: false },
  help_requested: { in_app: true, email: true },
  new_lead: { in_app: true, email: true },
  new_message: { in_app: true, email: false },
};

// ── Step 5 — pricing. EVERYTHING shown (names, prices, message/knowledge/behavior
//    limits, rescraping, branding, AND the Wix App Market plan id used for checkout)
//    comes live from the bot_subscription_plans table via /api/subscription_plans —
//    the same source the portal /billing page reads. Nothing here is hardcoded.
//    Only the presentation order + which tier is highlighted live in code (as they do
//    on /billing), since the DB has no "order"/"featured" column. Card styling mirrors
//    the portal /billing page (rk-* design). ──
const WXO_WIX_APP_ID = process.env.REACT_APP_WIX_APP_ID;
const WXO_TIER_ORDER = ['Smart', 'Boost', 'Ultimo'];
const WXO_FEATURED_TIER = 'Boost';

// Apps + Channels logo rows for the plan cards — copied 1:1 from the portal /billing
// page (BotBilling.js) so the look, the hover tooltip, and the "+N" click-to-open
// popover behave identically.
const WXO_LOGOS_SHOWN = 5;
const WXO_APPS = [
  { key: 'calendly', label: 'Calendly', logo: '/calendly-logo.png' },
  { key: 'hubspot', label: 'HubSpot', logo: '/hubspot-logo.png' },
  { key: 'stripe', label: 'Stripe', logo: '/stripe-logo.png' },
  { key: 'mailchimp', label: 'Mailchimp', logo: '/mailchimp-logo.png' },
  { key: 'calcom', label: 'Cal.com', logo: '/calcom-logo.png' },
  { key: 'zendesk', label: 'Zendesk', logo: '/zendesk-logo.png' },
];
// Channels shown in the plan cards, ordered by what owners actually care about:
// the messaging/social channels + the generic web embed FIRST, then the specific
// website platforms LAST (Wix/Webflow/WordPress, plus the deploy-guide platforms
// like Shopify/Squarespace/WooCommerce) — most owners don't choose the agent for those.
const WXO_PRIORITY_CHANNELS = [
  { key: 'instagram', label: 'Instagram', logo: '/instagram-logo.webp' },
  { key: 'facebook', label: 'Facebook', logo: '/facebook-logo.webp' },
  { key: 'whatsapp', label: 'WhatsApp', logo: '/whatsapp-logo.webp' },
  { key: 'slack', label: 'Slack', logoComponent: SlackLogo },
  { key: 'telegram', label: 'Telegram', icon: faTelegram, color: '#229ED9' },
  { key: 'website', labelKey: 'billing_channel_website_widget', icon: faCode },
  { key: 'sharelink', labelKey: 'billing_channel_chat_link', icon: faLink },
];
// Website builders / CMS / e-commerce platforms — shown last (Wix first among them
// as it's the owner's own platform, then the rest of the deploy-guide platforms).
const WXO_SITE_PLATFORMS = [
  { key: 'wix', label: 'Wix', logo: '/wix-logo.png' },
  { key: 'webflow', label: 'Webflow', logo: '/webflow-logo.png' },
  { key: 'wordpress', label: 'WordPress', logo: '/wordpress-logo.png' },
];
const WXO_GUIDE_CHANNELS = CHANNEL_GUIDES.map((g) => ({ key: g.key, label: g.name, logo: g.logo }));
const WXO_CHANNELS = [...WXO_PRIORITY_CHANNELS, ...WXO_SITE_PLATFORMS, ...WXO_GUIDE_CHANNELS];
const WXO_CHANNEL_ANY = { key: 'any-web', icon: faCode, labelKey: 'billing_chip_any_website', color: '#5616ea' };
const WXO_APP_ANY = { key: 'any-app', icon: faBolt, labelKey: 'billing_chip_any_app', color: '#5616ea' };

function WxoLogoChip({ item }) {
  const { t } = useTranslation();
  const label = item.labelKey ? t(item.labelKey) : item.label;
  return (
    <span className="rk-logo" data-tip={label} aria-label={label}>
      {item.logoComponent ? (
        <item.logoComponent size={19} />
      ) : item.logo ? (
        <img src={item.logo} alt={label} />
      ) : (
        <FontAwesomeIcon icon={item.icon} style={item.color ? { color: item.color } : undefined} />
      )}
    </span>
  );
}

// A row of the most important logos with a "+N" chip. The popover (listing only the
// hidden items) opens on hover OR click of the chip; a small close delay lets the mouse
// travel chip -> popover without flicker.
function WxoLogoRow({ items, lead, max = WXO_LOGOS_SHOWN }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const shown = items.slice(0, max);
  const extra = items.length - max;
  const hidden = items.slice(max);
  const cancelClose = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  const scheduleClose = () => { cancelClose(); timer.current = setTimeout(() => setOpen(false), 90); };
  useEffect(() => () => cancelClose(), []);
  return (
    <div className="rk-logo-row">
      <div className="rk-logos">
        {lead && <WxoLogoChip key={lead.key} item={lead} />}
        {shown.map((it) => <WxoLogoChip key={it.key} item={it} />)}
        {extra > 0 && (
          <span className="rk-logo-more-wrap">
            <button
              type="button"
              className="rk-logo-more"
              onMouseEnter={() => { cancelClose(); setOpen(true); }}
              onMouseLeave={scheduleClose}
              onClick={() => setOpen((o) => !o)}
              aria-expanded={open}
              aria-label={t('billing_show_n_more', { count: extra })}
            >
              +{extra}
            </button>
            {open && (
              <div className="rk-logo-pop" role="tooltip" onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
                {hidden.map((it) => (
                  <span key={it.key} className="rk-logo-pop-item">
                    <WxoLogoChip item={it} />
                    <span className="rk-logo-pop-label">{it.labelKey ? t(it.labelKey) : it.label}</span>
                  </span>
                ))}
              </div>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

function base64ToFile(base64, filename) {
  const arr = base64.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8 = new Uint8Array(n);
  while (n--) u8[n] = bstr.charCodeAt(n);
  return new File([u8], filename, { type: mime });
}

// The /wix-start pricing step shows the Wix-specific price (Wix charges can differ
// from Stripe). Falls back to the standard monthly_price while wix_monthly_price is
// still unset in the DB, so the cards never break before the values are filled in.
function wixDisplayPrice(plan) {
  if (!plan) return 0;
  const p = plan.wix_monthly_price;
  return Number(p != null ? p : plan.monthly_price) || 0;
}

export default function WixOnboarding() {
  const { t, i18n } = useTranslation();
  const demo = new URLSearchParams(window.location.search).get('demo') === '1';
  const currentLanguage = (i18n.language || 'en').split('-')[0];
  const currentLangLabel = (SUPPORTED_LANGUAGES.find((l) => l.code === currentLanguage) || { label: 'English' }).label;

  // step/bot restored from localStorage (real only; demo never remembers)
  // step/bot/scanning are NOT seeded from localStorage anymore — the Wix entry is
  // resolved server-side and handed to the mount effect, which sets them authoritatively.
  // Seeding from localStorage caused a deleted bot to linger and fire capabilities on it.
  const [step, setStep] = useState(0);
  const TOTAL_STEPS = 6; // account, connect, design, test, notifications, pricing

  // Start the loader immediately on a real Wix entry (the instance handshake) so the
  // purple loader is the first thing painted, with no flash of the account step before it.
  const [pageLoading, setPageLoading] = useState(() => {
    if (demo) return false;
    const p = new URLSearchParams(window.location.search);
    return !!(p.get('wix_instance_id') || p.get('instance') || p.get('code'));
  });
  const [appNotInstalled, setAppNotInstalled] = useState(false);
  const [requiresAccessToken, setRequiresAccessToken] = useState(false);

  const [authMode, setAuthMode] = useState('register');
  const [firstName, setFirstName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [uid, setUid] = useState(null);
  const [token, setToken] = useState(null);
  const [wixInstanceId, setWixInstanceId] = useState('');

  const [url, setUrl] = useState('');
  const [wixSite, setWixSite] = useState(null);     // { name, url } of the connected Wix site
  const [siteId, setSiteId] = useState('');         // Wix meta-site-id (for the pricing checkout URL)
  const [docFiles, setDocFiles] = useState([]);      // uploaded documents [{ name, url, size }] (ingested on build)
  const [docModalOpen, setDocModalOpen] = useState(false);
  const [docSync, setDocSync] = useState({ active: false, phase: 'idle', done: 0, total: 0 });
  const [bot, setBot] = useState(null);

  // step 4 — notification preferences (events x channels matrix) + email recipients
  const [notifyPrefs, setNotifyPrefs] = useState(WXO_DEFAULT_PREFS);
  const [notificationEmails, setNotificationEmails] = useState(['']);
  const [notifySaveAttempted, setNotifySaveAttempted] = useState(false);

  // step 5 — pricing / free trial (Wix App Market checkout, then poll the bot's plan)
  const [billingCycle, setBillingCycle] = useState('YEAR');
  const [isCheckingPlan, setIsCheckingPlan] = useState(false);
  const [planCheckError, setPlanCheckError] = useState('');
  const [trialStarted, setTrialStarted] = useState(false);
  const [plans, setPlans] = useState([]);          // bot_subscription_plans rows (cards are DB-driven)
  const [plansLoading, setPlansLoading] = useState(true);

  // detection
  const [scanning, setScanning] = useState(false);
  const [scanStep, setScanStep] = useState(0); // cycles the scanning status line
  const [subApps, setSubApps] = useState(null);
  const [cmsAvailable, setCmsAvailable] = useState(false); // site has public Wix CMS collections
  const [selected, setSelected] = useState(() => new Set());
  const [provisioning, setProvisioning] = useState(false);

  // background website training
  const [training, setTraining] = useState({ active: false, phase: 'idle', found: 0, done: 0 });
  const [productSync, setProductSync] = useState({ active: false, phase: 'idle', done: 0, total: 0, count: 0 });
  const productPollRef = useRef(null);       // done-strip hide timer
  const productKnowledgeRef = useRef(null);  // catalog sync's knowledge_id; WS progress matches on it
  const docKnowledgeRef = useRef(new Set()); // uploaded-document knowledge_ids (WS progress matches on them)
  const notifyEmailUserEditedRef = useRef(false); // once true, stop auto-syncing email slot 0 to the account email
  const planPollRef = useRef(null);          // setInterval handle for the post-checkout plan poll
  const productSyncStartedRef = useRef(false); // guards the catalog sync so it fires once per bot
  const cmsSyncStartedRef = useRef(false);     // guards the CMS discover+sync so it fires once per bot
  const trainingReqRef = useRef(null);
  const trainingKnowledgeRef = useRef(new Set());
  // Business context is generated ONCE, after every ingest source we're waiting on
  // (website / documents / products) has finished. These refs coordinate that.
  const bizContextFiredRef = useRef(false);      // fire the generation exactly once per bot
  const srcExpectedRef = useRef(new Set());      // sources we wait for: 'website' | 'docs' | 'products'
  const srcDoneRef = useRef(new Set());          // sources that have finished
  const detectionDoneRef = useRef(false);        // capability detection done -> products expectation is known

  // design
  const [widget, setWidget] = useState(DEFAULT_WIDGET);
  const iconInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const avatarInputRef = useRef(null);
  const dirtyWidgetRef = useRef(new Set());
  const widgetFetchedRef = useRef(false);

  // design — interactive preview chat (type + send, with a canned bot reply)
  const [previewInput, setPreviewInput] = useState('');
  const [previewMsgs, setPreviewMsgs] = useState([]);  // [{ role: 'user'|'bot', text }]
  const [previewMenuOpen, setPreviewMenuOpen] = useState(false); // header three-dots menu
  const [liveModalOpen, setLiveModalOpen] = useState(false);     // "Talk to a human" info modal
  const previewBodyRef = useRef(null);
  const previewReplyRef = useRef(null);
  const previewMenuRef = useRef(null);

  const [err, setErr] = useState('');
  const [emailExists, setEmailExists] = useState(false); // render a "Try login" link instead of plain text
  const [loading, setLoading] = useState(false);

  const scannedBotRef = useRef(null);

  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);
  const handleLanguageChange = (lang) => { i18n.changeLanguage(lang); localStorage.setItem('language', lang); };

  // ── scroll lock + ResizeObserver-loop guard ──
  useEffect(() => {
    const html = document.documentElement, body = document.body;
    const ph = html.style.overflow, pb = body.style.overflow;
    html.style.overflow = 'hidden'; body.style.overflow = 'hidden';

    // Defer ResizeObserver callbacks by a frame so the embedded chat preview
    // re-measuring on mount can't trigger the benign "ResizeObserver loop"
    // error that trips CRA's dev overlay. (stopImmediatePropagation loses to
    // the overlay's own listener, so we neutralise the loop at the source.)
    const Native = window.ResizeObserver;
    if (Native && !Native.__wxoWrapped) {
      const Wrapped = class extends Native {
        constructor(cb) {
          super((entries, obs) => { window.requestAnimationFrame(() => { try { cb(entries, obs); } catch (_) { /* noop */ } }); });
        }
      };
      Wrapped.__wxoWrapped = true;
      window.ResizeObserver = Wrapped;
    }

    return () => {
      html.style.overflow = ph; body.style.overflow = pb;
      if (Native) window.ResizeObserver = Native;
      if (productPollRef.current) clearTimeout(productPollRef.current);
    };
  }, []);

  useEffect(() => {
    if (!langOpen) return;
    const onDown = (e) => { if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [langOpen]);

  // ── Wix entry: App.js's /resolve already made the decision and handed off here via
  //    the URL. We just APPLY it — no continue_installation, no re-checking. ──
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const wixInstance = params.get('wix_instance_id');
    const codeParam = params.get('code');
    const errorParam = params.get('error');
    if (demo) { setWixInstanceId(DEMO_INSTANCE); return; } // demo: skip the Wix redirect gate
    if (errorParam) {
      // resolve returned ERROR (or a legacy error param) → a Wix-only screen, never login.
      if (errorParam === 'not_installed' || errorParam === 'app_not_installed') setAppNotInstalled(true);
      else setRequiresAccessToken(true);
      setPageLoading(false);
      return;
    }
    if (wixInstance) {
      setWixInstanceId(wixInstance);
      if (params.get('register') === '1') {
        // REGISTER: no account for this site → account-creation step (email prefilled).
        const email = params.get('email');
        if (email) setUserEmail(email);
        setStep(0);
        setPageLoading(false);
        return;
      }
      // ONBOARDING: resolve authenticated us (token in localStorage). Pull the step +
      // agent for THIS site straight from the DB (keyed on the instance) — never from
      // browser storage — so it's identical across devices and survives cleared storage.
      const t = localStorage.getItem('token');
      const u = localStorage.getItem('user_id');
      if (t) setToken(t);
      if (u) setUid(Number(u));
      (async () => {
        try {
          const r = await authFetch(`${API}wix/onboarding/state?wix_instance_id=${encodeURIComponent(wixInstance)}`);
          const j = await r.json();
          if (j && j.bot_id) { setBot(j.bot_id); setStep(Math.max(Number(j.step) || 2, 2)); }
          else { setBot(null); setStep(1); }
        } catch (e) { setBot(null); setStep(1); }
        finally { setPageLoading(false); }
      })();
      return;
    }
    if (codeParam) { handleInstallApp(codeParam); return; }  // OAuth install grant (separate)
    setRequiresAccessToken(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Connected Wix site name + URL (for the connect header + URL pre-fill). Runs for both
  // demo and real once the instance id is known — the demo path skips handleWixInstance,
  // which is why the pre-fill was missing there.
  useEffect(() => {
    if (!wixInstanceId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}wix/onboarding/site_info?wix_instance_id=${encodeURIComponent(wixInstanceId)}`);
        const j = await r.json();
        if (cancelled || !j) return;
        if (j.name || j.url) setWixSite({ name: j.name || '', url: j.url || '' });
        if (j.site_id) setSiteId(j.site_id);    // Wix meta-site-id for the pricing checkout
        if (j.url) setUrl((u) => u || j.url);   // pre-fill only if still empty (don't stomp edits)
      } catch (e) { /* best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [wixInstanceId]);

  // Step 4: default the first notification-email slot to the account email,
  // keeping it synced until the user edits the slot themselves.
  useEffect(() => {
    if (!userEmail || notifyEmailUserEditedRef.current) return;
    setNotificationEmails((prev) => { const next = prev.length ? prev.slice() : ['']; next[0] = userEmail; return next; });
  }, [userEmail]);

  // Step 5: stop the plan poll on unmount and whenever we leave the pricing step.
  useEffect(() => () => { if (planPollRef.current) clearInterval(planPollRef.current); }, []);
  useEffect(() => {
    if (step !== 5 && planPollRef.current) { clearInterval(planPollRef.current); planPollRef.current = null; setIsCheckingPlan(false); }
  }, [step]);

  // Step 5: load the subscription plans (the cards are entirely DB-driven). Public
  // endpoint, exactly what the portal /billing page reads.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API}subscription_plans`);
        const data = await r.json();
        if (alive && Array.isArray(data)) setPlans(data);
      } catch (e) { /* best-effort */ }
      finally { if (alive) setPlansLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  // Step 5: group the DB plans by name into month/year (like /billing) + compute savings.
  const monthlyByName = useMemo(() => { const m = {}; plans.forEach((p) => { if (p.billing_interval !== 'year') m[p.name] = p; }); return m; }, [plans]);
  const yearlyByName = useMemo(() => { const m = {}; plans.forEach((p) => { if (p.billing_interval === 'year') m[p.name] = p; }); return m; }, [plans]);
  const pricingTiers = useMemo(() => WXO_TIER_ORDER.map((n) => monthlyByName[n]).filter(Boolean), [monthlyByName]);
  const pricingSavings = useCallback((name) => {
    const m = monthlyByName[name]; const y = yearlyByName[name];
    const mp = wixDisplayPrice(m); const yp = wixDisplayPrice(y);
    if (m && y && mp > 0) return { pct: Math.round(((mp - yp) / mp) * 100), yearlyMonthly: yp, monthly: mp };
    return null;
  }, [monthlyByName, yearlyByName]);

  // OAuth install grant (?code=). Separate from the dashboard ?instance= entry (which
  // App.js routes via /resolve). This path fires on a FRESH install AND on every
  // permission re-consent ("Update" in the Wix App Market) — both redirect here with a
  // code. A site that already has an agent must therefore go to the dashboard, never
  // back into the build flow (that would create a second agent and rebind the site).
  async function handleInstallApp(code) {
    try {
      setPageLoading(true);
      const r = await fetch(`${API}auth/wix/install_app?code=${encodeURIComponent(code)}`);
      const j = await r.json();
      if (j.requires_access_token) { setRequiresAccessToken(true); setPageLoading(false); return; }
      if (j.url) setUrl(j.url);
      if (j.wix_instance_id) setWixInstanceId(j.wix_instance_id);
      setUserEmail(j.user_email || '');
      if (r.ok && j.has_account && j.has_bot) {
        // Existing agent bound to this site (e.g. the owner just approved updated
        // permissions). Log in and open the portal dashboard — mirrors the
        // auth/wix/resolve DASHBOARD action and the legacy WixRegister behaviour.
        if (j.token) localStorage.setItem('token', j.token);
        if (j.user_id) localStorage.setItem('user_id', String(j.user_id));
        // Same skip as the other sign-in entries: one agent → SingleAgentEntryGate
        // forwards /my-bots to /overview. The flag is in sessionStorage precisely
        // so it survives this hard navigation.
        markSingleAgentEntry();
        window.location.href = '/my-bots';
        return;
      }
      if (r.ok && j.has_account && j.token) {
        setUid(j.user_id); setToken(j.token);
        localStorage.setItem('token', j.token);
        setStep(1);                 // installed + account, no agent yet → build step
      } else {
        setStep(0);                 // no account → account creation
      }
      setPageLoading(false);
    } catch (e) { console.error('install_app failed', e); setPageLoading(false); }
  }

  // Business context is one of the most important prompt inputs, but the onboarding
  // creates the bot BEFORE its content is ingested. We generate it ONCE — from the real
  // website + document + product content — after every source we're waiting on has
  // finished (see maybeGenerateBusinessContext). The backend pulls the current content.
  const generateBusinessContext = useCallback(async (botId) => {
    if (!botId) return; // runs in demo too — demo uses the real backend/training
    try {
      await authFetch(`${API}wix/onboarding/generate_business_context`, {
        method: 'POST', body: JSON.stringify({ bot_id: botId }),
      });
    } catch (e) { console.error('business context generation failed', e); }
  }, []);

  // Fire the single business-context generation when (and only when) detection has run
  // AND every expected ingest source has finished. No-op if nothing was ingested or it
  // already fired. Sources mark themselves done via srcDoneRef as their WS jobs complete.
  const maybeGenerateBusinessContext = useCallback((botId) => {
    if (bizContextFiredRef.current || !detectionDoneRef.current) return;
    const expected = srcExpectedRef.current;
    if (expected.size === 0) return; // nothing to summarise -> leave the context empty
    for (const src of expected) { if (!srcDoneRef.current.has(src)) return; }
    bizContextFiredRef.current = true;
    generateBusinessContext(botId);
  }, [generateBusinessContext]);

  // A source (website / docs / products) finished -> record it and try to generate.
  const markSourceDone = useCallback((name, botId) => {
    srcDoneRef.current.add(name);
    maybeGenerateBusinessContext(botId);
  }, [maybeGenerateBusinessContext]);

  // ── single WebSocket: bot readiness + website-training progress ──
  useEffect(() => {
    if (!uid || !bot) return;
    const raw = process.env.REACT_APP_API_WEBSOCKET_URL;
    let ws;
    try {
      if (!raw) throw new Error('WebSocket URL missing');
      ws = new WebSocket(raw.replace(/^http/, 'ws'));
    } catch (e) { console.error('WS init failed', e); return; }

    ws.addEventListener('open', () => {
      ws.send(JSON.stringify({ type: 'init', user_id: uid, token: token || localStorage.getItem('token') }));
    });

    ws.addEventListener('message', async (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); } catch { return; }

      // (a) website discovery finished -> kick off scraping
      if (msg.type === 'find_new_links_result' && msg.request_id === trainingReqRef.current) {
        const links = (msg.new_found_links || []).slice(0, 50); // first scrape: cap at 50 pages
        if (!links.length) {
          setTraining({ active: true, phase: 'none', found: 0, done: 0 });
          setTimeout(() => setTraining((tr) => ({ ...tr, active: false })), 2600);
          markSourceDone('website', bot);   // no pages -> website source is "done" (empty)
          return;
        }
        setTraining({ active: true, phase: 'training', found: links.length, done: 0 });
        try {
          const r = await authFetch(`${API}scrape_link`, {
            method: 'POST',
            body: JSON.stringify({ bot_id: bot, user_id: uid, urls: links }),
          });
          const j = await r.json();
          (j.knowledge_ids || []).forEach((id) => trainingKnowledgeRef.current.add(id));
          if (!trainingKnowledgeRef.current.size) markSourceDone('website', bot); // nothing to track
        } catch (e) { console.error('scrape_link failed', e); markSourceDone('website', bot); }
        return;
      }
      if (msg.type === 'find_new_links_error' && msg.request_id === trainingReqRef.current) {
        setTraining({ active: true, phase: 'none', found: 0, done: 0 });
        setTimeout(() => setTraining((tr) => ({ ...tr, active: false })), 2600);
        markSourceDone('website', bot);
        return;
      }

      // (c) product catalog sync -> climbing bar (chunked WS: { status, done, total })
      if (msg.knowledge_id != null && msg.knowledge_id === productKnowledgeRef.current) {
        if (msg.status === 'completed') {
          setProductSync((p) => {
            const count = p.total || p.done || 0;
            return { active: true, phase: 'done', done: count, total: count, count };
          });
          if (productPollRef.current) clearTimeout(productPollRef.current);
          productPollRef.current = setTimeout(() => setProductSync((x) => ({ ...x, active: false })), DONE_HIDE_MS);
          markSourceDone('products', bot);
        } else if (msg.status === 'failed') {
          setProductSync({ active: false, phase: 'idle', done: 0, total: 0, count: 0 });
          markSourceDone('products', bot);
        } else if (msg.total) {  // 'processing' tick with counts
          setProductSync({ active: true, phase: 'importing', done: msg.done || 0, total: msg.total, count: msg.done || 0 });
        }
        return;
      }

      // (b) per-page scrape status -> advance the bar
      if (msg.knowledge_id != null && trainingKnowledgeRef.current.has(msg.knowledge_id)) {
        if (msg.status === 'completed' || msg.status === 'failed') {
          setTraining((tr) => {
            const done = Math.min(tr.found, tr.done + 1);
            if (done >= tr.found && tr.found > 0) {
              setTimeout(() => setTraining((x) => ({ ...x, active: false })), DONE_HIDE_MS);
              markSourceDone('website', bot); // website fully scraped
              return { ...tr, done, phase: 'done' };
            }
            return { ...tr, done };
          });
        }
        return;
      }

      // (e) per-document ingest status -> advance the documents bar
      if (msg.knowledge_id != null && docKnowledgeRef.current.has(msg.knowledge_id)) {
        if (msg.status === 'completed' || msg.status === 'failed') {
          setDocSync((d) => {
            const done = Math.min(d.total, d.done + 1);
            if (done >= d.total && d.total > 0) {
              setTimeout(() => setDocSync((x) => ({ ...x, active: false })), DONE_HIDE_MS);
              markSourceDone('docs', bot); // documents ingested
              return { ...d, done, phase: 'done' };
            }
            return { ...d, done };
          });
        }
        return;
      }

    });

    return () => ws.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, bot, demo]);

  // (Resume is now decided server-side by /api/auth/wix/resolve and handed to the mount
  //  effect above — per site, validated against this instance's bot_id. No client-side
  //  re-validation needed.)

  // ── detect Wix sub-apps once per bot, as soon as the bot exists ──
  useEffect(() => {
    if (step === 1 && bot && subApps === null && scannedBotRef.current !== bot) {
      scannedBotRef.current = bot;
      scanCapabilities(bot);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, bot, subApps]);

  // ── cycle the scanning status line while detection runs ──
  useEffect(() => {
    if (!scanning) { setScanStep(0); return undefined; }
    const id = setInterval(() => setScanStep((s) => (s + 1) % 4), 1500);
    return () => clearInterval(id);
  }, [scanning]);

  // ── persist progress to the DB only (the single source of truth; real only). No
  //    browser storage — the step must survive device changes + cleared storage. Only
  //    the resumable steps (>=2, agent built) are saved; the build/reveal step is
  //    transient and recovered from the instance's bot_id by /onboarding/state. ──
  useEffect(() => {
    if (demo || step < 2 || !bot || !uid || !token) return;
    authFetch(`${API}wix/onboarding/progress`, { method: 'PUT', body: JSON.stringify({ step, bot_id: bot }) }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, bot, uid, token, demo]);

  // ── fetch the widget config when entering the Design step ──
  useEffect(() => {
    if (step !== 2 || !bot || widgetFetchedRef.current) return;
    widgetFetchedRef.current = true;
    (async () => {
      try {
        const res = await fetch(`${API}widget_configuration/${encodeURIComponent(bot)}`);
        if (!res.ok) return;
        const d = await res.json();
        setWidget((w) => ({
          ...w,
          themeColor: d.theme_color?.slice(0, 7) || w.themeColor,
          headerColor: d.header_font_color?.slice(0, 7) || w.headerColor,
          hoverColor: d.button_hover_color || w.hoverColor,
          pulsing: d.pulsing ?? w.pulsing,
          widgetHorizontalAlignment: d.widget_horizontal_alignment || w.widgetHorizontalAlignment,
          widgetBorderRadius: d.widget_border_radius ?? w.widgetBorderRadius,
          widgetSize: d.widget_size ?? w.widgetSize,
          headerText: d.header_text || w.headerText,
          welcomeMessage: Array.isArray(d.welcome_message) ? (d.welcome_message[0] || w.welcomeMessage) : (d.welcome_message || w.welcomeMessage),
          icon: d.widget_icon_path || '',
          logo: d.header_icon_path || '',
          avatar: d.avatar_icon_path || '',
          predefinedQuestions: Array.isArray(d.predefined_questions) ? d.predefined_questions.filter(Boolean).slice(0, 3) : [],
          ...Object.fromEntries([...dirtyWidgetRef.current].map(key => [key, w[key]])),
        }));
      } catch (e) { console.error('widget config fetch failed', e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, bot, demo]);

  // ── design preview: keep the chat scrolled to the latest message ──
  useEffect(() => {
    const el = previewBodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [previewMsgs]);
  // Clear the pending canned-reply timer on unmount.
  useEffect(() => () => { if (previewReplyRef.current) clearTimeout(previewReplyRef.current); }, []);
  // Close the preview's three-dots menu on an outside click.
  useEffect(() => {
    if (!previewMenuOpen) return undefined;
    const onDown = (e) => { if (previewMenuRef.current && !previewMenuRef.current.contains(e.target)) setPreviewMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [previewMenuOpen]);

  // ── auth handlers ──
  async function handleRegister(e) {
    e.preventDefault();
    setErr(''); setEmailExists(false);
    const email = (userEmail || '').trim(), first = (firstName || '').trim();
    if (!EMAIL_RE.test(email)) { setErr(t('wxo_err_email')); return; }
    if (!first) { setErr(t('wxo_err_first_name')); return; }
    if (pw.length < 8) { setErr(t('wxo_err_password_short')); return; }
    if (pw !== pw2) { setErr(t('wxo_err_password_match')); return; }
    try {
      setLoading(true);
      const r = await fetch(`${API}register/`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password: pw, given_name: first, send_email: true, wix_instance_id: wixInstanceId, language: currentLanguage }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        if (j.detail === 'Email already registered') setEmailExists(true);
        else setErr(t('wxo_err_create_account'));
        return;
      }
      const j = await r.json();
      setUid(j.user_id); setToken(j.token);
      if (!demo) trackRegistration(j.user_id, 'wix');
      if (j.token) localStorage.setItem('token', j.token);
      setStep((s) => Math.max(s, 1));
    } catch { setErr(t('wxo_err_create_account')); }
    finally { setLoading(false); }
  }

  async function handleLogin(e) {
    e.preventDefault();
    setErr('');
    try {
      setLoading(true);
      const r = await fetch(`${API}login/`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userEmail, password: pw, wix_instance_id: wixInstanceId }),
      });
      if (r.ok) {
        const j = await r.json();
        setUid(j.user_id); setToken(j.token);
        if (j.token) localStorage.setItem('token', j.token);
        if (j.first_name) setFirstName(j.first_name);
        setStep((s) => Math.max(s, 1));
      } else {
        const j = await r.json().catch(() => ({}));
        setErr(j.detail || t('wxo_err_invalid_login'));
      }
    } catch { setErr(t('wxo_err_generic')); }
    finally { setLoading(false); }
  }

  // ── connect: instant bot + (optional) background website training ──
  function domainName(input) {
    try {
      const u = new URL(input.startsWith('http') ? input : 'https://' + input);
      const host = u.hostname.replace(/^www\./, '');
      const parts = host.split('.');
      return parts.length >= 2 ? parts[parts.length - 2] : host;
    } catch { return 'agent'; }
  }

  async function startWebsiteTraining(botId, siteUrl) {
    srcExpectedRef.current.add('website');   // business context waits for this
    setTraining({ active: true, phase: 'searching', found: 0, done: 0 });
    trainingKnowledgeRef.current = new Set();
    const reqId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    trainingReqRef.current = reqId;
    try {
      await authFetch(`${API}find_new_links`, { method: 'POST', body: JSON.stringify({ bot_id: botId, url: siteUrl, request_id: reqId }) });
    } catch (e) { console.error('find_new_links failed', e); setTraining((tr) => ({ ...tr, active: false })); markSourceDone('website', botId); }
  }

  // Documents: ingest the uploaded files (Azure URLs) the same way the portal does
  // (ingest_document_urls -> durable queue -> per-doc WS {knowledge_id, status}).
  async function startDocumentIngest(botId, urls) {
    srcExpectedRef.current.add('docs');      // business context waits for this
    setDocSync({ active: true, phase: 'processing', done: 0, total: urls.length });
    docKnowledgeRef.current = new Set();
    try {
      const r = await authFetch(`${API}ingest_document_urls`, {
        method: 'POST', body: JSON.stringify({ bot_id: botId, user_id: uid, document_urls: urls }),
      });
      const j = await r.json();
      (j.knowledge_ids || []).forEach((id) => docKnowledgeRef.current.add(id));
      if (!docKnowledgeRef.current.size) markSourceDone('docs', botId); // nothing to track
    } catch (e) { console.error('document ingest failed', e); setDocSync((d) => ({ ...d, active: false })); markSourceDone('docs', botId); }
  }

  // Upload modal -> append the chosen files. The modal hands us the Azure URLs plus a
  // parallel metadata array ([{ name, url, size }]); fall back to URL-derived names.
  function handleDocsAdded(urls, files) {
    const items = (files && files.length)
      ? files
      : (urls || []).map((u) => ({ url: u, name: decodeURIComponent((u.split('?')[0].split('/').pop()) || 'document') }));
    setDocFiles((prev) => [...prev, ...items]);
  }

  function removeDoc(idx) {
    setDocFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  // ── Wix Stores catalog sync (reuses the durable products-sync API; progress comes
  //    over the same knowledge-status WS as website training — handled below) ──
  async function startProductSync(botId) {
    srcExpectedRef.current.add('products');   // business context waits for this
    setProductSync({ active: true, phase: 'importing', done: 0, total: 0, count: 0 });
    try {
      const r = await authFetch(`${API}products/sources/wix/sync?bot_id=${encodeURIComponent(botId)}`, { method: 'POST' });
      const j = await r.json().catch(() => ({}));
      productKnowledgeRef.current = j.knowledge_id != null ? j.knowledge_id : null;
      if (productKnowledgeRef.current == null) markSourceDone('products', botId); // nothing to track
    } catch (e) {
      console.error('product sync failed', e);
      setProductSync({ active: false, phase: 'idle', done: 0, total: 0, count: 0 });
      markSourceDone('products', botId);
    }
  }

  // ── Wix CMS (site content): discover the site's public native collections and,
  //    if any, surface the always-on card + enable + sync them all in the
  //    background. Knowledge-only (no skill), so this just mirrors them like the
  //    products catalog. Reuses the existing apps/wix/cms/* endpoints; the owner
  //    refines which collections are used later in AI Studio -> Apps. ──
  async function startCmsSync(botId) {
    try {
      const r = await fetch(`${API}apps/wix/cms/collections?bot_id=${encodeURIComponent(botId)}`);
      if (!r.ok) { setCmsAvailable(false); return; }
      const j = await r.json().catch(() => ({}));
      const cols = j.collections || [];
      if (!cols.length) { setCmsAvailable(false); return; }
      setCmsAvailable(true);
      await Promise.all(cols.filter((c) => !c.enabled).map((c) =>
        authFetch(`${API}apps/wix/cms/collections/${encodeURIComponent(c.collection_id)}?bot_id=${encodeURIComponent(botId)}`, {
          method: 'POST', body: JSON.stringify({ enabled: true }),
        }).catch(() => {})));
    } catch (e) { console.error('cms sync failed', e); setCmsAvailable(false); }
  }

  async function createBotAndContinue() {
    setErr('');
    const site = url.trim();
    try {
      setLoading(true);
      const r = await authFetch(`${API}wix/onboarding/create_bot`, {
        method: 'POST',
        body: JSON.stringify({ wix_instance_id: wixInstanceId, bot_name: site ? domainName(site) : 'My agent', url: site || null }),
      });
      const j = await r.json();
      if (!j.bot_id) throw new Error('no bot id');
      const botId = j.bot_id;
      scannedBotRef.current = null;
      productSyncStartedRef.current = false;
      cmsSyncStartedRef.current = false;
      setCmsAvailable(false);
      // Reset the business-context coordinator for this fresh bot.
      bizContextFiredRef.current = false;
      srcExpectedRef.current = new Set();
      srcDoneRef.current = new Set();
      detectionDoneRef.current = false;
      setSubApps(null);
      setScanning(true);
      setBot(botId);
      // Kick the ingest jobs off IN PARALLEL right away: website training + document
      // ingest run alongside capability detection (which then starts products + CMS).
      if (site) startWebsiteTraining(botId, site);
      if (docFiles.length) startDocumentIngest(botId, docFiles.map((f) => f.url));
    } catch (e) {
      console.error(e);
      setErr(t('wxo_err_build'));
    } finally { setLoading(false); }
  }

  const scanCapabilities = useCallback(async (botId) => {
    setScanning(true); setErr('');
    try {
      const [capResponse, cmsResponse, prefResponse] = await Promise.all([
        authFetch(`${API}wix/onboarding/capabilities?bot_id=${encodeURIComponent(botId)}`),
        authFetch(`${API}apps/wix/cms/collections?bot_id=${encodeURIComponent(botId)}`),
        authFetch(`${API}apps/wix/knowledge_preferences?bot_id=${encodeURIComponent(botId)}`),
        prepareWixBrand(botId).catch(() => ({status: 'unavailable'})),
      ]);
      if (!capResponse.ok) throw new Error('capabilities');
      const detected = (await capResponse.json()).sub_apps || [];
      const collections = cmsResponse.ok ? (await cmsResponse.json()).collections || [] : [];
      setSubApps(detected);
      setCmsAvailable(collections.length > 0);
      const preferences = prefResponse.ok ? await prefResponse.json() : null;
      const chosen = new Set([...detected.flatMap(sa => sa.flows.map(f => f.key)), ...(collections.length ? [CMS_FLOW] : [])]);
      if (preferences?.configured) {
        if (!preferences.products_enabled) chosen.delete(ALWAYS_ON_FLOW);
        if (!preferences.cms_enabled) chosen.delete(CMS_FLOW);
      }
      setSelected(chosen);
    } catch (e) {
      console.error('capabilities failed', e);
      setSubApps([]); setSelected(new Set()); setErr(t('wxo_err_build'));
    } finally {
      setScanning(false);
      detectionDoneRef.current = true;
    }
  }, [t]);

  function toggleFlow(key) {
    setSelected((prev) => { const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next; });
  }

  async function provisionAndContinue() {
    setErr('');
    const wantsProducts = selected.has(ALWAYS_ON_FLOW);
    const keys = [...selected].filter((k) => !KNOWLEDGE_FLOWS.has(k)); // wix_products is a default capability, not a skill
    // Preserve the wire name for compatibility; this now contains selected sources.
    const alwaysOn = [];
    if (wantsProducts) alwaysOn.push(ALWAYS_ON_FLOW);
    if (selected.has(CMS_FLOW)) alwaysOn.push(CMS_FLOW);
    try {
      setProvisioning(true);
      const preferences = await authFetch(`${API}apps/wix/knowledge_preferences?bot_id=${encodeURIComponent(bot)}`, {
        method: 'PUT', body: JSON.stringify({ products_enabled: wantsProducts, cms_enabled: selected.has(CMS_FLOW) }),
      });
      if (!preferences.ok) throw new Error('source preferences');
      {
        const provision = await authFetch(`${API}wix/onboarding/provision_skills?bot_id=${encodeURIComponent(bot)}`, {
          method: 'POST', body: JSON.stringify({ keys, always_on: alwaysOn }),
        });
        if (!provision.ok) throw new Error('provision failed');
      }
      // A source change invalidates the old summary, even if website training
      // finished before selection. Rebuild once the selected imports are ready.
      bizContextFiredRef.current = false;
      if (!wantsProducts) srcExpectedRef.current.delete('products');
      // Import only sources the owner has confirmed.
      if (wantsProducts && !productSyncStartedRef.current) { productSyncStartedRef.current = true; startProductSync(bot); }
      if (selected.has(CMS_FLOW) && !cmsSyncStartedRef.current) { cmsSyncStartedRef.current = true; startCmsSync(bot); }
      maybeGenerateBusinessContext(bot);
      widgetFetchedRef.current = false;
      setStep(2);
    } catch (e) { console.error('provision failed', e); setErr(t('wxo_err_provision')); }
    finally { setProvisioning(false); }
  }

  // ── design ──
  const setStyle = (k, v) => {
    dirtyWidgetRef.current.add(k);
    setWidget((w) => ({ ...w, [k]: v }));
  };
  // One handler for all three image uploads (icon / logo / avatar). Reads the
  // file as a data URL into the matching widget key; it's uploaded to Azure on
  // save (saveDesignAndContinue), mirroring the portal Design page.
  function handleImageUpload(e, key) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setStyle(key, ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  }
  async function uploadImage(base64) {
    const fd = new FormData();
    fd.append('file', base64ToFile(base64, `icon-${Date.now()}.png`));
    const res = await fetch(`${API}upload_image/`, { method: 'POST', body: fd });
    if (!res.ok) throw new Error('upload failed');
    const j = await res.json();
    return j.file_path;
  }
  async function saveDesignAndContinue() {
    setErr('');
    try {
      setLoading(true);
      // Upload any freshly-picked images (data URLs) to Azure first; keep
      // already-stored paths as-is. Mirrors the portal Design page.
      let iconPath = widget.icon;
      if (iconPath && iconPath.startsWith('data:image/')) iconPath = await uploadImage(iconPath);
      let logoPath = widget.logo;
      if (logoPath && logoPath.startsWith('data:image/')) logoPath = await uploadImage(logoPath);
      let avatarPath = widget.avatar;
      if (avatarPath && avatarPath.startsWith('data:image/')) avatarPath = await uploadImage(avatarPath);
      const updates = {
        theme_color: widget.themeColor,
        header_font_color: widget.headerColor,
        button_hover_color: widget.hoverColor,
        pulsing: widget.pulsing,
        widget_horizontal_alignment: widget.widgetHorizontalAlignment,
        widget_size: widget.widgetSize,
        widget_border_radius: widget.widgetBorderRadius,
        header_text: widget.headerText,
        welcome_message: [widget.welcomeMessage],
        predefined_questions: widget.predefinedQuestions.map(q => q.trim()).filter(Boolean).slice(0, 3),
        widget_icon_path: iconPath || '',
        header_icon_path: logoPath || '',
        avatar_icon_path: avatarPath || '',
      };
      const res = await fetch(`${API}widget_configuration/${encodeURIComponent(bot)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error('save failed');
      // Sync the uploaded Azure paths back into state (replacing the data URLs).
      if (iconPath !== widget.icon) setStyle('icon', iconPath || '');
      if (logoPath !== widget.logo) setStyle('logo', logoPath || '');
      if (avatarPath !== widget.avatar) setStyle('avatar', avatarPath || '');
      setStep(3);
    } catch (e) { console.error(e); setErr(t('wxo_design_save_error')); }
    finally { setLoading(false); }
  }

  // One image-upload control (icon / logo / avatar), matching the portal Design page.
  const imageUploadControl = (key, labelKey, ref) => (
    <div className="wxo-control-group">
      <div className="wxo-color-picker-container">
        <label className="wxo-widget-setting-title">{t(labelKey)}</label>
        <div className="wxo-logo-upload">
          {widget[key] && <img className="wxo-logo-preview" src={widget[key]} alt="" />}
          <button type="button" className="wxo-logo-upload-btn" onClick={() => ref.current && ref.current.click()}>
            <FontAwesomeIcon icon={faUpload} /><span>{widget[key] ? t('wxo_design_replace') : t('wxo_design_upload')}</span>
          </button>
          {widget[key] && <button type="button" className="wxo-logo-remove-btn" onClick={() => setStyle(key, '')} aria-label="remove"><FontAwesomeIcon icon={faXmark} /></button>}
          <input ref={ref} type="file" accept="image/*" onChange={(e) => handleImageUpload(e, key)} style={{ display: 'none' }} />
        </div>
      </div>
    </div>
  );

  // Design preview: send the typed message and answer with a canned preview reply.
  function sendPreview() {
    const text = previewInput.trim();
    if (!text) return;
    setPreviewMsgs((m) => [...m, { role: 'user', text }]);
    setPreviewInput('');
    if (previewReplyRef.current) clearTimeout(previewReplyRef.current);
    previewReplyRef.current = setTimeout(() => {
      setPreviewMsgs((m) => [...m, { role: 'bot', text: t('wxo_design_preview_reply') }]);
    }, 500);
  }
  // Preview three-dots menu: clear the chat back to just the welcome message.
  function clearPreviewChat() {
    if (previewReplyRef.current) clearTimeout(previewReplyRef.current);
    setPreviewMsgs([]);
    setPreviewInput('');
    setPreviewMenuOpen(false);
  }
  // "Talk to a human" (three-dots menu OR the inline pill) -> open the live-chat info
  // modal. Just a preview, no real handoff.
  function talkToHumanPreview() {
    setPreviewMenuOpen(false);
    setLiveModalOpen(true);
  }

  // ── render helpers ──
  const showBack = step > 0 && !scanning && !provisioning;
  const backBtn = showBack ? (
    <button className="wxo-back-inline" type="button" aria-label={t('wxo_back')}
      onClick={() => setStep((s) => Math.max(0, s - 1))}>
      <FontAwesomeIcon icon={faArrowLeft} /><span>{t('wxo_back')}</span>
    </button>
  ) : null;
  const titleRow = (heading) => <div className="wxo-title-row">{backBtn}{heading}</div>;

  const langToggle = (
    <div className="wxo-lang" ref={langRef}>
      <button type="button" className="wxo-lang-trigger" onClick={() => setLangOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={langOpen}>
        <FontAwesomeIcon icon={faGlobe} className="wxo-lang-icon" />
        <span className="wxo-lang-current">{currentLangLabel}</span>
      </button>
      {langOpen && (
        <div className="wxo-lang-menu" role="listbox">
          {SUPPORTED_LANGUAGES.map((l) => {
            const sel = l.code === currentLanguage;
            return (
              <button key={l.code} type="button" role="option" aria-selected={sel}
                className={`wxo-lang-option ${sel ? 'wxo-sel' : ''}`}
                onClick={() => { handleLanguageChange(l.code); setLangOpen(false); }}>
                <span className="wxo-lang-check">{sel && <FontAwesomeIcon icon={faCheck} />}</span>{l.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  // A circular ring (around a check) that depletes over DONE_HIDE_MS, showing the
  // time left before a finished strip auto-dismisses.
  const doneTimer = () => (
    <span className="wxo-strip-timer" aria-hidden="true">
      <svg viewBox="0 0 24 24" className="wxo-strip-timer-svg">
        <circle className="wxo-strip-timer-track" cx="12" cy="12" r="10" />
        <circle className="wxo-strip-timer-ring" cx="12" cy="12" r="10" style={{ animationDuration: `${DONE_HIDE_MS}ms` }} />
      </svg>
      <FontAwesomeIcon icon={faCheck} className="wxo-strip-timer-check" />
    </span>
  );

  const trainStrip = training.active ? (
    <div className="wxo-train-strip">
      <div className="wxo-train-inner">
        {training.phase === 'searching' && (<><span className="wxo-train-spin" /><span className="wxo-train-label">{t('wxo_train_searching')}</span></>)}
        {training.phase === 'training' && (
          <div className="wxo-train-prog">
            <div className="wxo-train-row">
              <span className="wxo-train-label">{t('wxo_train_progress', { done: training.done, total: training.found })}</span>
              <span className="wxo-train-pct">{Math.round((training.done / Math.max(1, training.found)) * 100)}%</span>
            </div>
            <div className="wxo-train-bar"><div className="wxo-train-fill" style={{ width: `${Math.round((training.done / Math.max(1, training.found)) * 100)}%` }} /></div>
          </div>
        )}
        {training.phase === 'done' && (<>{doneTimer()}<span className="wxo-train-label">{t('wxo_train_done', { count: training.found })}</span></>)}
        {training.phase === 'none' && (<span className="wxo-train-label">{t('wxo_train_none')}</span>)}
      </div>
    </div>
  ) : null;

  const productPct = Math.round((productSync.done / Math.max(1, productSync.total)) * 100);
  const productStrip = productSync.active ? (
    <div className="wxo-train-strip">
      <div className="wxo-train-inner">
        {productSync.phase === 'importing' && !productSync.total && (
          <><span className="wxo-train-spin" /><span className="wxo-train-label">{t('wxo_products_importing')}</span></>
        )}
        {productSync.phase === 'importing' && productSync.total > 0 && (
          <div className="wxo-train-prog">
            <div className="wxo-train-row">
              <span className="wxo-train-label">{t('wxo_products_progress', { done: productSync.done, total: productSync.total })}</span>
              <span className="wxo-train-pct">{productPct}%</span>
            </div>
            <div className="wxo-train-bar"><div className="wxo-train-fill" style={{ width: `${productPct}%` }} /></div>
          </div>
        )}
        {productSync.phase === 'done' && (
          <>{doneTimer()}<span className="wxo-train-label">{t('wxo_products_done', { count: productSync.count })}</span></>
        )}
      </div>
    </div>
  ) : null;

  const docPct = Math.round((docSync.done / Math.max(1, docSync.total)) * 100);
  const docStrip = docSync.active ? (
    <div className="wxo-train-strip">
      <div className="wxo-train-inner">
        {docSync.phase === 'processing' ? (
          <div className="wxo-train-prog">
            <div className="wxo-train-row">
              <span className="wxo-train-label">{t('wxo_docs_processing', { done: docSync.done, total: docSync.total })}</span>
              <span className="wxo-train-pct">{docPct}%</span>
            </div>
            <div className="wxo-train-bar"><div className="wxo-train-fill" style={{ width: `${docPct}%` }} /></div>
          </div>
        ) : (
          <>{doneTimer()}<span className="wxo-train-label">{t('wxo_docs_done', { count: docSync.total })}</span></>
        )}
      </div>
    </div>
  ) : null;

  const progressStrips = (training.active || productSync.active || docSync.active) ? (<>{trainStrip}{productStrip}{docStrip}</>) : null;

  // ── gate screens ──
  if (pageLoading) return <WixEntryLoader />;
  if (appNotInstalled || requiresAccessToken) {
    const notInstalled = appNotInstalled;
    return (
      <div className="wxo-scope wxo-wrapper">
        <img className="wxo-logo" src="/ultimo-bots-white.png" alt="Ultimo Bots" />
        {langToggle}
        <div className="wxo-stage">
          <div className="wxo-card">
            <h2 className="wxo-h2">{notInstalled ? t('wxo_gate_notfound_title') : t('wxo_gate_expired_title')}</h2>
            <p className="wxo-sub">{notInstalled ? t('wxo_gate_notfound_sub') : t('wxo_gate_expired_sub')}</p>
            <button className="wxo-btn" onClick={() => (window.location.href = 'https://wix.to/89ZoJRg')}>
              {notInstalled ? t('wxo_gate_install_btn') : t('wxo_gate_manage_btn')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const connectPhase = scanning ? 'scanning' : subApps !== null ? 'reveal' : 'input';
  const totalFlows = (subApps || []).reduce((n, sa) => n + sa.flows.length, 0);

  // ── Step 4 (notifications) — validation + save to notification_settings ──
  const notifyEmailRequired = WXO_NOTIFY_EVENTS.some((ev) => !wxoChannelLocked(ev.key, 'email') && Boolean(notifyPrefs[ev.key]?.email));
  const notifyCleanEmails = (notificationEmails || []).map((e) => String(e).trim()).filter((e) => e.length);
  const notifyInvalidEmails = notifyCleanEmails.filter((e) => !EMAIL_RE.test(e));
  const notifyEmailMissing = notifyEmailRequired && notifyCleanEmails.length === 0;
  const notifyEmailHasError = notifyEmailMissing || notifyInvalidEmails.length > 0;

  async function saveNotificationsAndContinue() {
    setNotifySaveAttempted(true);
    if (notifyEmailHasError) return;
    setErr('');
    setLoading(true);
    try {
      const valid = notifyCleanEmails.filter((e) => EMAIL_RE.test(e));
      const needs = (sec) => WXO_NOTIFY_EVENTS.some((ev) => ev.section === sec && !wxoChannelLocked(ev.key, 'email') && Boolean(notifyPrefs[ev.key]?.email));
      const payload = {
        prefs: notifyPrefs,
        emails_by_section: { live_chat: needs('live_chat') ? valid : [], leads: needs('leads') ? valid : [] },
        slack_webhooks_by_section: { live_chat: [], leads: [] },
        teams_webhooks_by_section: { live_chat: [], leads: [] },
        telegram_chat_ids_by_section: { live_chat: [], leads: [] },
      };
      if (bot) {
        try { await authFetch(`${API}notification_settings/${encodeURIComponent(bot)}`, { method: 'PUT', body: JSON.stringify(payload) }); }
        catch (e) { console.error('notification_settings PUT failed', e); }
      }
      setStep(5);
    } catch (e) { console.error(e); setStep(5); }
    finally { setLoading(false); }
  }

  // ── Step 5 (pricing) — open the Wix App Market checkout in a new tab, then poll the
  //    bot's plan until it flips off "Free" (the user can't message us back from Wix). ──
  function stopPlanPolling() {
    if (planPollRef.current) { clearInterval(planPollRef.current); planPollRef.current = null; }
    setIsCheckingPlan(false);
  }
  async function fetchBotPlanName() {
    if (!bot) throw new Error('no bot');
    const r = await fetch(`${API}bot?bot_id=${encodeURIComponent(bot)}&include_plan=true`);
    if (!r.ok) throw new Error('bot fetch failed');
    const j = await r.json();
    if (!demo) observeWixTrial(j);
    return j?.subscription_plan?.name ?? 'Free';
  }
  function startPlanPolling() {
    if (planPollRef.current || !bot) return;
    setPlanCheckError('');
    setIsCheckingPlan(true);
    planPollRef.current = setInterval(async () => {
      try {
        const name = await fetchBotPlanName();
        if (name && name !== 'Free') { stopPlanPolling(); setTrialStarted(true); }
      } catch (e) { setPlanCheckError(t('wxo_price_still_checking')); }
    }, 2000);
  }
  function startTrialCheckout(planName, wixPlanId) {
    if (!siteId || !wixPlanId) { console.error('pricing: missing siteId/wixPlanId, cannot open Wix checkout'); setErr(t('wxo_price_no_site')); return; }
    const checkoutUrl = `https://manage.wix.com/app-pricing-plans/${WXO_WIX_APP_ID}/plan/${wixPlanId}/payment-cycle/${billingCycle}/order-checkout?meta-site-id=${siteId}&origin=manage_apps_bizmgr`;
    if (!demo) beginWixCheckout(bot);
    window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
    startPlanPolling();
  }
  async function goToPortal() {
    // Leaving the onboarding for the portal = onboarding finished. Mark it so the next
    // Wix open routes straight to the dashboard (resolve checks bots.onboarding_completed).
    try { if (bot && !demo) await authFetch(`${API}wix/onboarding/complete`, { method: 'POST', body: JSON.stringify({ bot_id: bot }) }); } catch (e) { /* best-effort */ }
    const url = siteId ? `https://manage.wix.com/dashboard/${siteId}/app/${WXO_WIX_APP_ID}` : 'https://portal.ultimo-bots.com';
    // We're running inside the Wix dashboard iframe. Navigate the TOP window — NOT the
    // iframe ('_self'). That target URL *is* the Wix dashboard, so loading it into our
    // own iframe nests a second Wix dashboard inside the first (the double sidebar).
    try {
      if (window.top && window.top !== window.self) window.top.location.href = url;
      else window.location.href = url;
    } catch (e) {
      window.open(url, '_blank', 'noopener,noreferrer'); // sandbox blocked top-nav → new tab
    }
  }

  // Only one card is mounted at a time. Its width snaps per step (the fade-in
  // masks the change), and the anim key replays the entrance on each state.
  let stageW = 480;
  if (step === 0) stageW = 980; // account step: form + brand visual, two columns
  else if (step === 1) stageW = connectPhase === 'input' ? 540 : connectPhase === 'scanning' ? 640 : 880;
  else if (step === 2) stageW = 920;
  else if (step === 3) stageW = 920; // test step matches the design page's footprint
  else if (step === 4) stageW = 760; // notifications matrix
  else if (step === 5) stageW = 1000; // pricing — three plan cards side by side
  const animKey = step === 1 ? `c-${connectPhase}` : `s-${step}`;

  // The primary "continue" CTA for each step renders BELOW the card (centered, on the
  // purple shell) instead of inside it. Account (form submits) and Pricing (its own
  // plan CTAs) keep their in-card buttons, so they have no stage CTA.
  let stageCta = null;
  if (step === 1 && connectPhase === 'input') stageCta = { label: t('wxo_build_agent'), onClick: createBotAndContinue, busy: loading };
  else if (step === 1 && connectPhase === 'reveal') stageCta = { label: (totalFlows && selected.size) ? t('wxo_to_design') : t('wxo_continue'), onClick: provisionAndContinue, busy: provisioning };
  else if (step === 2) stageCta = { label: t('wxo_to_test'), onClick: saveDesignAndContinue, busy: loading };
  else if (step === 3) stageCta = { label: t('wxo_test_continue'), onClick: () => setStep(4), busy: false };
  else if (step === 4) stageCta = { label: t('wxo_notif_continue'), onClick: saveNotificationsAndContinue, busy: loading };

  return (
    <div className="wxo-scope wxo-wrapper">
      <img className="wxo-logo" src="/ultimo-bots-white.png" alt="Ultimo Bots" />
      {langToggle}
      <div className="wxo-step-ind">
        {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
          <span key={i} className={`wxo-step-dot ${i === step ? 'wxo-current' : i < step ? 'wxo-done' : ''}`} />
        ))}
      </div>

      <div className={`wxo-stage ${stageCta ? 'wxo-has-cta' : ''}`} style={{ maxWidth: stageW }}>
        <div className="wxo-card-anim" key={animKey}>

            {/* ── Step 0 — Account ── */}
            {step === 0 && (
              <WixAccountForm {...{ authMode, setAuthMode, firstName, setFirstName, userEmail, setUserEmail, pw, setPw, pw2, setPw2, emailExists, setEmailExists, err, setErr, loading, handleRegister, handleLogin }} />
            )}

            {/* ── Step 1 — Connect ── */}
            {step === 1 && (
              <div className={`wxo-card ${connectPhase === 'reveal' ? 'wxo-card-reveal' : ''} ${connectPhase !== 'input' ? 'wxo-card-skills' : ''}`}>
                {connectPhase === 'input' && (
                  <>
                    {titleRow(<h2 className="wxo-h2">{t('wxo_connect_title')}</h2>)}
                    <p className="wxo-sub">{t('wxo_connect_sub')}</p>
                    {wixSite?.name && (
                      <div className="wxo-site-banner">
                        <span className="wxo-site-dot" />
                        <span className="wxo-site-banner-text">{t('wxo_site_connected_to')} <strong>{wixSite.name}</strong></span>
                      </div>
                    )}
                    <div className="wxo-sources">
                      <div className="wxo-source">
                        <div className="wxo-source-top">
                          <span className="wxo-source-ic"><FontAwesomeIcon icon={faGlobe} /></span>
                          <span className="wxo-source-label">{t('wxo_src_website')}</span>
                        </div>
                        <TextField label={t('wxo_url_placeholder')} type="text" value={url} onChange={(e) => setUrl(e.target.value)} fullWidth variant="outlined" size="small" sx={FIELD_SX} />
                      </div>
                      <div className={`wxo-source wxo-source-docs ${docFiles.length ? 'wxo-has' : ''}`} role="button" tabIndex={0}
                        onClick={() => setDocModalOpen(true)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDocModalOpen(true); } }}>
                        <div className="wxo-source-top">
                          <span className="wxo-source-ic"><FontAwesomeIcon icon={faFileArrowUp} /></span>
                          <span className="wxo-source-label">{t('wxo_src_documents')}</span>
                          {docFiles.length > 0
                            ? <button type="button" className="wxo-source-clear" onClick={(e) => { e.stopPropagation(); setDocFiles([]); }}>{t('wxo_docs_clear')}</button>
                            : <FontAwesomeIcon icon={faArrowRight} className="wxo-source-arrow" />}
                        </div>
                        {docFiles.length > 0 ? (
                          <>
                            <ul className="wxo-doc-list">
                              {docFiles.map((f, i) => (
                                <li key={`${f.url}-${i}`} className="wxo-doc-item">
                                  <FontAwesomeIcon icon={faFileLines} className="wxo-doc-ic" />
                                  <span className="wxo-doc-name" title={f.name}>{f.name}</span>
                                  <button type="button" className="wxo-doc-rm" onClick={(e) => { e.stopPropagation(); removeDoc(i); }} aria-label={t('wxo_docs_remove')}><FontAwesomeIcon icon={faXmark} /></button>
                                </li>
                              ))}
                            </ul>
                            <span className="wxo-doc-more"><FontAwesomeIcon icon={faPlus} /> {t('wxo_docs_more')}</span>
                          </>
                        ) : (
                          <span className="wxo-source-hint">{t('wxo_docs_hint')}</span>
                        )}
                      </div>
                    </div>
                    {err && <div className="wxo-err">{err}</div>}
                    <p className="wxo-privacy">{t('wxo_privacy')}</p>
                  </>
                )}

                {connectPhase === 'scanning' && (
                  <div className="wxo-skills-stage">
                    <div className="wxo-skills-grid-bg" aria-hidden="true" />
                    <div className="wxo-skills-glow" aria-hidden="true" />
                    <div className="wxo-scan2">
                      <div className="wxo-radar" aria-hidden="true">
                        <span className="wxo-radar-ring" />
                        <span className="wxo-radar-ring wxo-radar-ring-2" />
                        <span className="wxo-radar-ring wxo-radar-ring-3" />
                        <span className="wxo-radar-sweep" />
                        <span className="wxo-radar-core"><img src="/wix-logo.png" alt="" /></span>
                      </div>
                      <h2 className="wxo-skills-h2">{t('wxo_scan_title')}</h2>
                      <div className="wxo-scan-status">
                        <span className="wxo-scan-pulse" />
                        <span className="wxo-scan-status-text" key={scanStep}>{t(`wxo_scan_step_${scanStep + 1}`)}</span>
                      </div>
                    </div>
                  </div>
                )}

                {connectPhase === 'reveal' && (() => {
                  // One concise card per detected skill (flows flattened across sub-apps);
                  // each carries its Wix app logo + label so cards read on their own.
                  // The always-on "Show products" card sorts last among the detected
                  // flows; the always-on "Site content" (Wix CMS) card sits after it.
                  const allFlows = (subApps || [])
                    .flatMap((sa) => sa.flows.map((f) => ({ ...f, appLabelKey: sa.label_key, logo: sa.logo })))
                    .sort((a, b) => (a.key === ALWAYS_ON_FLOW ? 1 : 0) - (b.key === ALWAYS_ON_FLOW ? 1 : 0));
                  if (cmsAvailable) {
                    allFlows.push({ key: CMS_FLOW, appLabelKey: 'wix_sub_cms', label_key: 'skill_flow_wix_cms_label', icon: faDatabase });
                  }
                  return (
                    <div className="wxo-skills-stage wxo-skills-reveal">
                      <div className="wxo-skills-head">
                        {titleRow(<h2 className="wxo-h2">{allFlows.length === 0 ? t('wxo_reveal_title_none') : t('wxo_skills_title')}</h2>)}
                        <p className="wxo-sub">{allFlows.length ? t('wxo_skills_sub') : t('wxo_reveal_sub_none')}</p>
                      </div>
                      <div className="wxo-skills-panel">
                        <div className="wxo-skills-grid-bg" aria-hidden="true" />
                        <div className="wxo-skills-glow" aria-hidden="true" />
                      {allFlows.length > 0 ? (() => {
                        const renderCard = (f, i) => {
                          const knowledge = KNOWLEDGE_FLOWS.has(f.key);
                          const isCms = f.key === CMS_FLOW;
                          const on = selected.has(f.key);
                          const knowledgeDescKey = isCms ? 'wxo_cms_optional_desc' : 'wxo_products_optional_desc';
                          const card = (
                            <button type="button"
                              className={`wxo-skill-card ${on ? 'wxo-on' : ''} `}
                              style={{ '--i': isCms ? 0 : i }}
                              aria-pressed={on}
                              onClick={() => toggleFlow(f.key)}>
                              <span className={`wxo-skill-logo ${f.icon ? 'wxo-skill-logo-icon' : ''}`}>
                                {f.icon ? <FontAwesomeIcon icon={f.icon} /> : <img src={`/wix/${f.logo}`} alt="" />}
                              </span>
                              <span className="wxo-skill-text">
                                <span className="wxo-skill-app">{t(f.appLabelKey)}</span>
                                <span className="wxo-skill-name">{t(f.label_key)}</span>
                                <span className="wxo-skill-desc">{knowledge ? t(knowledgeDescKey) : t(f.desc_key)}</span>
                              </span>
                              <span className="wxo-skill-mark" aria-hidden="true">
                                <span className="wxo-skill-check"><FontAwesomeIcon icon={faCheck} /></span>
                              </span>
                            </button>
                          );
                          return cloneElement(card, { key: f.key });
                        };
                        // Keep knowledge sources together, with the same selection interaction.
                        const normalFlows = allFlows.filter((f) => !KNOWLEDGE_FLOWS.has(f.key));
                        const knowledgeFlows = allFlows.filter((f) => KNOWLEDGE_FLOWS.has(f.key));
                        return (
                          <div className="wxo-skills-scroll">
                            <div className="wxo-skills-cards">
                              {normalFlows.map((f, i) => renderCard(f, i))}
                              {knowledgeFlows.length > 0 && (
                                <div className="wxo-skills-divider"><span>{t('wxo_knowledge_sources')}</span></div>
                              )}
                              {knowledgeFlows.map((f, i) => renderCard(f, normalFlows.length + i))}
                            </div>
                          </div>
                        );
                      })() : (<div className="wxo-skills-empty">{t('wxo_empty_detect')}</div>)}
                      </div>
                      <div className="wxo-skills-foot">
                        {allFlows.length > 0 && <div className="wxo-skills-count">{t('wxo_selected_count', { n: allFlows.filter((f) => selected.has(f.key)).length, m: allFlows.length })}</div>}
                        {err && <div className="wxo-err">{err}</div>}
                        {progressStrips}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* ── Step 2 — Design ── */}
            {step === 2 && (
              <div className="wxo-card">
                {titleRow(<h2 className="wxo-h2">{t('wxo_design_title')}</h2>)}
                <p className="wxo-sub">{t('wxo_design_sub')}</p>
                <div className="wxo-preview-card">
                  <div className="wxo-user-settings">
                    <div className="wxo-control-group">
                      <TextField label={t('wxo_design_header')} value={widget.headerText} onChange={(e) => setStyle('headerText', e.target.value)}
                        fullWidth variant="outlined" inputProps={{ maxLength: 60 }}
                        helperText={`${(widget.headerText || '').length}/60`} sx={FIELD_SX} />
                    </div>
                    <div className="wxo-control-group">
                      <TextField label={t('wxo_design_welcome')} value={widget.welcomeMessage} onChange={(e) => setStyle('welcomeMessage', e.target.value)}
                        fullWidth multiline minRows={2} maxRows={4} variant="outlined" inputProps={{ maxLength: 300 }}
                        helperText={`${(widget.welcomeMessage || '').length}/300`} sx={FIELD_SX} />
                    </div>
                    <div className="wxo-control-group">
                      <div className="wxo-color-picker-container">
                        <label className="wxo-widget-setting-title">{t('wxo_design_theme')}</label>
                        <input type="color" value={widget.themeColor} onChange={(e) => setStyle('themeColor', e.target.value)} />
                      </div>
                    </div>
                    <div className="wxo-control-group">
                      <div className="wxo-color-picker-container">
                        <label className="wxo-widget-setting-title">{t('wxo_design_header_color')}</label>
                        <input type="color" value={widget.headerColor} onChange={(e) => setStyle('headerColor', e.target.value)} />
                      </div>
                    </div>
                    {imageUploadControl('icon', 'wxo_design_icon', iconInputRef)}
                    {imageUploadControl('logo', 'wxo_design_logo', logoInputRef)}
                    {imageUploadControl('avatar', 'wxo_design_avatar', avatarInputRef)}
                  </div>

                  <div className="wxo-divider" />

                  <div className="wxo-preview-area">
                    <div className="wxo-chat-bubble" style={{ background: widget.icon ? 'transparent' : widget.themeColor }}>
                      {widget.icon ? <img src={widget.icon} alt="" /> : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="58%" viewBox="0 0 24 24" fill="#fff"><path fillRule="evenodd" d="M12 2.25c-2.429 0-4.817.178-7.152.521C2.87 3.061 1.5 4.795 1.5 6.741v6.018c0 1.946 1.37 3.68 3.348 3.97.877.129 1.761.234 2.652.316V21a.75.75 0 0 0 1.28.53l4.184-4.183a.39.39 0 0 1 .266-.112c2.006-.05 3.982-.22 5.922-.506 1.978-.29 3.348-2.023 3.348-3.97V6.741c0-1.947-1.37-3.68-3.348-3.97A49.145 49.145 0 0 0 12 2.25ZM8.25 8.625a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25Zm2.625 1.125a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Zm4.875-1.125a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25Z" clipRule="evenodd" /></svg>
                      )}
                    </div>
                    <div className="wxo-chat-window">
                      <div className="wxo-chat-header" style={{ backgroundColor: widget.themeColor }}>
                        <div className="wxo-chat-header-title">
                          <span className="wxo-chat-header-brand">
                            {widget.logo && <img className="wxo-chat-logo" src={widget.logo} alt="" />}
                            <span className="wxo-chat-title" style={{ color: widget.headerColor }}>{widget.headerText || 'Chat with us'}</span>
                          </span>
                          <span className="wxo-chat-actions" ref={previewMenuRef}>
                            <button type="button" className="wxo-chat-kebab" style={{ color: widget.headerColor }}
                              aria-label={t('wxo_design_menu_more')} aria-haspopup="menu" aria-expanded={previewMenuOpen}
                              onClick={() => setPreviewMenuOpen((o) => !o)}>
                              <FontAwesomeIcon icon={faEllipsis} />
                            </button>
                            <button type="button" className="wxo-chat-close" style={{ color: widget.headerColor }} aria-label="close"><FontAwesomeIcon icon={faXmark} /></button>
                            {previewMenuOpen && (
                              <div className="wxo-chat-menu" role="menu">
                                <button type="button" className="wxo-chat-menu-item" role="menuitem" onClick={clearPreviewChat}>
                                  <FontAwesomeIcon icon={faTrash} /> {t('wxo_design_menu_clear')}
                                </button>
                                <button type="button" className="wxo-chat-menu-item" role="menuitem" onClick={talkToHumanPreview}>
                                  <FontAwesomeIcon icon={faHeadset} /> {t('wxo_design_menu_human')}
                                </button>
                              </div>
                            )}
                          </span>
                        </div>
                      </div>
                      <div className="wxo-chat-body" ref={previewBodyRef}>
                        <div className="wxo-chat-row bot">
                          {widget.avatar && <img className="wxo-chat-avatar" src={widget.avatar} alt="" />}
                          <div className="wxo-chat-msg">{widget.welcomeMessage || 'Hi, how can I assist you?'}</div>
                        </div>
                        {previewMsgs.map((m, i) => (
                          <div key={i} className={`wxo-chat-row ${m.role}`}>
                            {m.role === 'bot' && widget.avatar && <img className="wxo-chat-avatar" src={widget.avatar} alt="" />}
                            <div className={`wxo-chat-msg ${m.role === 'user' ? 'wxo-chat-msg-user' : 'wxo-chat-msg-md'}`}
                              style={m.role === 'user' ? { background: widget.themeColor, borderColor: widget.themeColor } : undefined}>
                              {m.role === 'bot' ? <ReactMarkdown>{m.text}</ReactMarkdown> : m.text}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="wxo-chat-footer">
                        <WixQuestionEditor questions={widget.predefinedQuestions} onChange={questions => setStyle('predefinedQuestions', questions)} />
                        <div className="wxo-live-cta-row">
                          <button type="button" className="wxo-live-cta-btn" onClick={() => setLiveModalOpen(true)}>
                            <FontAwesomeIcon icon={faHeadset} /><span>{t('wxo_design_menu_human')}</span>
                          </button>
                        </div>
                        <div className="wxo-powered"><span className="wxo-powered-text">Powered by Ultimo Bots</span></div>
                        <div className="wxo-input-send">
                          <input className="wxo-chat-input" type="text" value={previewInput} placeholder={t('chat_input_placeholder')}
                            onChange={(e) => setPreviewInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); sendPreview(); } }} />
                          <button type="button" className="wxo-chat-send" style={{ backgroundColor: widget.themeColor, color: widget.headerColor }}
                            onClick={sendPreview} disabled={!previewInput.trim()}><FontAwesomeIcon icon={faArrowUp} /></button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {err && <div className="wxo-err">{err}</div>}
                {progressStrips}
              </div>
            )}

            {/* ── Step 3 — Test ── */}
            {step === 3 && (
              <div className="wxo-card wxo-card-test">
                {titleRow(<h2 className="wxo-h2">{t('wxo_test_title')}</h2>)}
                <p className="wxo-sub">{t('wxo_test_sub')}</p>
                {step === 3 && bot ? (
                  <div className="wxo-test-chat">
                    <ChatModal
                      embedded
                      open
                      onClose={() => {}}
                      bot={{ id: bot, knowledge_count: 1 }}
                      currentMessageCount={0}
                      messagePlanLimit={999999}
                      planName="trial"
                      openSubscriptionModalFromChat={() => {}}
                      onMessageCountChange={() => {}}
                      liveCtaAlways
                      onLiveCta={() => setLiveModalOpen(true)}
                      // Onboarding = meeting the agent, not tuning it. The
                      // "Improve this answer" CTA stays a portal surface.
                      showImprove={false}
                    />
                  </div>
                ) : (
                  <div className="wxo-scan"><div className="wxo-scan-ring" /><span>{t('wxo_test_preparing')}</span></div>
                )}
                {progressStrips}
              </div>
            )}

            {/* ── Step 4 — notifications (events x channels matrix + email recipients) ── */}
            {step === 4 && (
              <div className="wxo-card">
                {titleRow(<h2 className="wxo-h2">{t('wxo_notif_title')}</h2>)}
                <p className="wxo-sub">{t('wxo_notif_sub')}</p>

                <div className="wxo-notify-matrix">
                  <div className="wxo-notify-row wxo-notify-head">
                    <div className="wxo-notify-event-col" />
                    {WXO_NOTIFY_CHANNELS.map((ch) => (
                      <div key={ch.key} className="wxo-notify-channel-col">
                        <FontAwesomeIcon icon={ch.icon} className="wxo-notify-channel-icon" />
                        <span className="wxo-notify-channel-label">{t(ch.labelKey)}</span>
                      </div>
                    ))}
                  </div>
                  {WXO_NOTIFY_EVENTS.map((ev) => {
                    const row = notifyPrefs[ev.key] || {};
                    return (
                      <div key={ev.key} className={`wxo-notify-row ${ev.highlight ? 'wxo-notify-row-hi' : ''}`}>
                        <div className="wxo-notify-event-col">
                          <div className="wxo-notify-event-title"><FontAwesomeIcon icon={ev.icon} className="wxo-notify-event-icon" />{t(ev.titleKey)}</div>
                          <div className="wxo-notify-event-desc">{t(ev.descKey)}</div>
                        </div>
                        {WXO_NOTIFY_CHANNELS.map((ch) => {
                          const locked = wxoChannelLocked(ev.key, ch.key);
                          const checkbox = (
                            <Checkbox
                              checked={locked ? false : Boolean(row[ch.key])}
                              disabled={locked}
                              onChange={() => {
                                setNotifySaveAttempted(false);
                                setNotifyPrefs((prev) => ({ ...prev, [ev.key]: { ...(prev[ev.key] || {}), [ch.key]: !prev[ev.key]?.[ch.key] } }));
                              }}
                              sx={{ '&.Mui-checked': { color: '#5616ea' }, '&:hover': { backgroundColor: '#5616ea15' }, '&.Mui-disabled': { color: '#e0e0e6' }, padding: '4px' }}
                            />
                          );
                          return (
                            <div key={ch.key} className="wxo-notify-channel-col">
                              {locked ? (
                                <Tooltip title={t('wxo_notif_locked')} arrow><span style={{ cursor: 'not-allowed', display: 'inline-flex' }}><span style={{ pointerEvents: 'none' }}>{checkbox}</span></span></Tooltip>
                              ) : checkbox}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>

                <div className="wxo-notify-portal">
                  <span className="wxo-notify-portal-label">{t('wxo_notif_also')}</span>
                  <span className="wxo-notify-chips">
                    <span className="wxo-notify-chip"><TelegramBrandIcon size={15} />Telegram</span>
                    <span className="wxo-notify-chip"><SlackBrandIcon size={15} />Slack</span>
                    <span className="wxo-notify-chip"><TeamsBrandIcon size={15} />Teams<span className="wxo-notify-soon">{t('wxo_notif_soon')}</span></span>
                  </span>
                </div>

                {notifyEmailRequired && (
                  <div className="wxo-notify-recipients">
                    <div className="wxo-notify-recipients-head"><FontAwesomeIcon icon={faEnvelope} style={{ color: '#5616ea' }} /><span>{t('wxo_notif_recipients')}</span></div>
                    <div className="wxo-notify-emails">
                      {(notificationEmails.length ? notificationEmails : ['']).map((value, idx) => {
                        const list = notificationEmails.length ? notificationEmails : [''];
                        const trimmed = String(value).trim();
                        const showError = notifySaveAttempted && trimmed.length > 0 && !EMAIL_RE.test(trimmed);
                        return (
                          <div key={idx} className="wxo-notify-email-row">
                            <TextField
                              label={t('wxo_notif_email_n', { n: idx + 1 })}
                              value={value}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (idx === 0) notifyEmailUserEditedRef.current = true;
                                setNotifySaveAttempted(false);
                                setNotificationEmails((prev) => { const next = prev.slice(); next[idx] = v; return next; });
                              }}
                              type="text" fullWidth variant="outlined" size="small" error={showError} placeholder="you@example.com"
                              InputProps={{ endAdornment: list.length > 1 ? (<InputAdornment position="end"><IconButton aria-label="Remove" edge="end" size="small" onClick={() => { setNotifySaveAttempted(false); setNotificationEmails((prev) => { const next = prev.filter((_, i) => i !== idx); return next.length ? next : ['']; }); }}><FontAwesomeIcon icon={faTrash} style={{ fontSize: 13 }} /></IconButton></InputAdornment>) : null }}
                              sx={FIELD_SX}
                            />
                          </div>
                        );
                      })}
                    </div>
                    {notificationEmails.length < 3 && (
                      <button type="button" className="wxo-notify-add-email" onClick={() => { setNotifySaveAttempted(false); setNotificationEmails((prev) => (prev.length >= 3 ? prev : [...prev, ''])); }} title={t('wxo_notif_add_email')}>
                        <FontAwesomeIcon icon={faPlus} />
                      </button>
                    )}
                    {notifySaveAttempted && notifyEmailHasError && (
                      <div className="wxo-notify-error"><FontAwesomeIcon icon={faTriangleExclamation} /><span>{notifyEmailMissing ? t('wxo_notif_err_missing') : t('wxo_notif_err_invalid')}</span></div>
                    )}
                  </div>
                )}

                {err && <div className="wxo-err">{err}</div>}
              </div>
            )}

            {/* ── Step 5 — pricing / start free trial (Wix App Market checkout, styled like /billing) ── */}
            {step === 5 && (
              <div className="wxo-card wxo-pricing">
                {trialStarted ? (
                  <div className="wxo-trial-done">
                    <div className="wxo-trial-done-ic"><FontAwesomeIcon icon={faCheck} /></div>
                    <h2 className="wxo-h2">{t('wxo_price_done_title')}</h2>
                    <p className="wxo-sub">{t('wxo_price_done_sub')}</p>
                    <button className="wxo-btn" onClick={goToPortal}>{t('wxo_price_to_portal')} <FontAwesomeIcon icon={faArrowRight} /></button>
                  </div>
                ) : isCheckingPlan ? (
                  <div className="wxo-trial-wait">
                    <div className="wxo-scan-ring" />
                    <h2 className="wxo-h2">{t('wxo_price_detecting_title')}</h2>
                    <p className="wxo-sub">{t('wxo_price_detecting_sub')}</p>
                    {planCheckError && <div className="wxo-err">{planCheckError}</div>}
                    <div className="wxo-trial-wait-actions">
                      <button className="wxo-btn-ghost" onClick={stopPlanPolling}>{t('wxo_price_back')}</button>
                      <button className="wxo-btn" onClick={async () => { try { const n = await fetchBotPlanName(); if (n !== 'Free') { stopPlanPolling(); setTrialStarted(true); } else { setPlanCheckError(t('wxo_price_still_checking')); } } catch { setPlanCheckError(t('wxo_price_still_checking')); } }}>{t('wxo_price_refresh')}</button>
                    </div>
                  </div>
                ) : (
                  <>
                    {titleRow(<h2 className="wxo-h2">{t('wxo_price_title')}</h2>)}

                    <div className="rk-billing-toggle">
                      <span className={`rk-billing-thumb${billingCycle === 'YEAR' ? ' right' : ''}`} aria-hidden />
                      <button type="button" className={billingCycle === 'MONTH' ? 'active' : ''} onClick={() => setBillingCycle('MONTH')}>{t('monthly')}</button>
                      <button type="button" className={billingCycle === 'YEAR' ? 'active' : ''} onClick={() => setBillingCycle('YEAR')}>{t('yearly')}</button>
                    </div>

                    {plansLoading && !pricingTiers.length ? (
                      <div className="wxo-trial-wait"><div className="wxo-scan-ring" /></div>
                    ) : (
                    <div className="rk-plans">
                      {pricingTiers.map((mRow) => {
                        const name = mRow.name;
                        const yRow = yearlyByName[name];
                        const row = billingCycle === 'YEAR' ? (yRow || mRow) : mRow;
                        const sv = pricingSavings(name);
                        const featured = name === WXO_FEATURED_TIER;
                        const rescrape = mRow.automatic_rescraping;
                        const removeBranding = !!mRow.remove_branding;
                        const wixId = mRow.wix_app_plan_id;
                        return (
                          <div key={name} className={`rk-plan${featured ? ' featured' : ''}`}>
                            {featured && <span className="rk-plan-flag">{t('recommended_start')}</span>}
                            <div className="rk-plan-name">{name}</div>
                            {billingCycle === 'YEAR' && sv ? (
                              <div>
                                <div className="rk-plan-price">
                                  <s className="rk-plan-old">${sv.monthly}</s>
                                  <span className="rk-plan-arrow">→</span>
                                  ${sv.yearlyMonthly}
                                  <span className="rk-plan-save">−{sv.pct}%</span>
                                </div>
                                <div className="rk-plan-billed">{t('billing_per_month_billed_annually')}</div>
                              </div>
                            ) : (
                              <div className="rk-plan-price">${wixDisplayPrice(row)}<span>{t('wxo_price_per_month')}</span></div>
                            )}
                            <div className="rk-plan-limits">
                              <div className="rk-plan-limit"><FontAwesomeIcon icon={faComments} /><span>{t('wxo_price_limit_messages')}</span><strong>{(mRow.message_limit ?? 0).toLocaleString('de-CH')}</strong></div>
                              <div className="rk-plan-limit"><FontAwesomeIcon icon={faBookOpen} /><span>{t('wxo_price_limit_knowledge')}</span><strong>{(mRow.document_limit ?? 0).toLocaleString('de-CH')}</strong></div>
                              <div className="rk-plan-limit"><FontAwesomeIcon icon={faPuzzlePiece} /><span>{t('wxo_price_limit_skills')}</span><strong>{mRow.skill_limit ?? 0}</strong></div>
                              <div className="rk-plan-limit"><FontAwesomeIcon icon={faSliders} /><span>{t('wxo_price_limit_behaviors')}</span><strong>{mRow.behavior_limit ?? 0}</strong></div>
                            </div>
                            <button type="button" className={`rk-plan-cta ${featured ? 'featured' : ''}`} disabled={isCheckingPlan || !wixId} onClick={() => startTrialCheckout(name, wixId)}>{t('start_free_trial')}</button>
                            <div className="rk-plan-div" />
                            <div className="rk-plan-sec"><span>{t('billing_section_apps')}</span></div>
                            <WxoLogoRow items={WXO_APPS} lead={WXO_APP_ANY} />
                            <div className="rk-plan-div" />
                            <div className="rk-plan-sec"><span>{t('billing_section_channels')}</span></div>
                            <WxoLogoRow items={WXO_CHANNELS} lead={WXO_CHANNEL_ANY} />
                            <div className="rk-plan-div" />
                            <ul className="rk-plan-feats">
                              <li><FontAwesomeIcon icon={faCheck} className="rk-feat-ok" /> {t('wxo_price_feat_lead')}</li>
                              <li className={rescrape !== 'none' ? '' : 'off'}><FontAwesomeIcon icon={rescrape !== 'none' ? faCheck : faMinus} className={rescrape !== 'none' ? 'rk-feat-ok' : ''} /> {rescrape === 'daily' ? t('wxo_price_feat_rescrape_daily') : rescrape === 'monthly' ? t('wxo_price_feat_rescrape_monthly') : t('wxo_price_feat_rescrape')}</li>
                              <li className={removeBranding ? '' : 'off'}><FontAwesomeIcon icon={removeBranding ? faCheck : faMinus} className={removeBranding ? 'rk-feat-ok' : ''} /> {t('wxo_price_feat_branding')}</li>
                            </ul>
                          </div>
                        );
                      })}
                    </div>
                    )}

                    {err && <div className="wxo-err">{err}</div>}
                    <p className="wxo-price-note">{t('cancel_note')}</p>
                    <p className="wxo-price-free"><button type="button" onClick={goToPortal}>{t('wxo_price_free_link')}</button></p>
                  </>
                )}
              </div>
            )}

            {stageCta && (
              <div className="wxo-stage-cta">
                <button type="button" className="wxo-cta-below" onClick={stageCta.onClick} disabled={stageCta.busy}>
                  <span>{stageCta.label}</span>
                  {stageCta.busy ? <span className="wxo-spin" /> : <FontAwesomeIcon icon={faArrowRight} />}
                </button>
              </div>
            )}

        </div>
      </div>

      <UploadFileModal
        open={docModalOpen}
        onClose={() => setDocModalOpen(false)}
        onSubmit={handleDocsAdded}
      />

      {liveModalOpen && (
        <div className="wxo-live-modal-overlay" onClick={() => setLiveModalOpen(false)}>
          <div className="wxo-live-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="wxo-skills-grid-bg" aria-hidden="true" />
            <div className="wxo-skills-glow" aria-hidden="true" />
            <button type="button" className="wxo-live-modal-close" onClick={() => setLiveModalOpen(false)} aria-label={t('close')}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
            <div className="wxo-live-modal-inner">
              <span className="wxo-live-modal-icon"><FontAwesomeIcon icon={faHeadset} /></span>
              <h3 className="wxo-live-modal-title">{t('wxo_live_modal_title')}</h3>
              <p className="wxo-live-modal-body">{t('wxo_live_modal_body')}</p>
              <button type="button" className="wxo-live-modal-ok" onClick={() => setLiveModalOpen(false)}>{t('wxo_live_modal_ok')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
